import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export const KpiCard = ({
  label,
  value,
  Icon,
  hint,
  tone = 'default',
  testId,
  to,
}) => {
  const toneStyles = {
    default: 'text-foreground',
    primary: 'text-[hsl(var(--primary))]',
    warning: 'text-[hsl(var(--warning))]',
    destructive: 'text-[hsl(var(--destructive))]',
    success: 'text-[hsl(var(--success))]',
  };
  const accentBar = {
    default: 'bg-border',
    primary: 'bg-[hsl(var(--primary))]/70',
    warning: 'bg-[hsl(var(--warning))]/70',
    destructive: 'bg-[hsl(var(--destructive))]/70',
    success: 'bg-[hsl(var(--success))]/70',
  };

  const interactive = !!to;
  const cardInner = (
    <Card
      className={cn(
        'relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))] transition-colors',
        interactive
          ? 'hover:border-[hsl(var(--primary))]/50 group-focus-visible:border-[hsl(var(--primary))]'
          : 'hover:border-[hsl(var(--primary))]/30'
      )}
      data-testid={testId}
    >
      <span
        aria-hidden
        className={cn('metallic absolute left-0 top-0 h-full w-[2px]', accentBar[tone])}
      />
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
              {label}
            </div>
            <div
              className={cn('mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums truncate', toneStyles[tone])}
              data-testid={testId ? `${testId}-value` : undefined}
            >
              {value}
            </div>
            {hint ? (
              <div className="mt-1.5 text-xs text-muted-foreground truncate">
                {hint}
              </div>
            ) : null}
          </div>
          {Icon ? (
            <div
              className={cn(
                'hidden sm:flex shrink-0 size-10 lg:size-8 2xl:size-10 rounded-md border border-border bg-[hsl(var(--muted))] items-center justify-center transition-colors',
                toneStyles[tone],
                interactive && 'group-hover:border-[hsl(var(--primary))]/50'
              )}
            >
              <Icon size={20} strokeWidth={1.75} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );

  if (interactive) {
    return (
      <Link
        to={to}
        className="group block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--background))]"
        data-testid={testId ? `${testId}-link` : undefined}
        aria-label={typeof label === 'string' ? `${label}: ${value}` : undefined}
      >
        {cardInner}
      </Link>
    );
  }
  return cardInner;
};

export default KpiCard;
